import { TUI_CONFIRM } from '@taiga-ui/kit';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { EMPTY, filter, forkJoin, of, switchMap, tap } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { OutkeepPermissionService } from '../permission/outkeep-permission.service';
import { XAlertService } from '../x-alert.service';
import { RequestsApiService } from './requests-api.service';
import { RequestService } from './request.service';
import { RequestModel } from '@core/models';
import { catchError } from 'rxjs/operators';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  RequestAddressesServiceTranslations,
  RequestAddressesServiceTranslationsPayload,
} from '@core/types';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class RequestAddressesService {
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly requestApiService = inject(RequestsApiService);
  private readonly xAlertService = inject(XAlertService);
  private readonly requestService = inject(RequestService);
  readonly permissionService = inject(OutkeepPermissionService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly $request = this.requestService.$request;
  private translates: RequestAddressesServiceTranslationsPayload = {
    [RequestAddressesServiceTranslations.SetAddressDialogLabel]:
      'Вы действительно готовы подтвердить адрес выдачи? Изменить его будет уже нельзя.',
    [RequestAddressesServiceTranslations.SetAddressDialogConfirm]:
      'Подтверждаю',
    [RequestAddressesServiceTranslations.SetAddressDialogCancel]: 'Отмена',
    [RequestAddressesServiceTranslations.SetAddressErrorMultiAll]:
      'Произошла ошибка при сохранении всех адресов выдачи',
    [RequestAddressesServiceTranslations.SetAddressErrorMultiSome]:
      'Произошла ошибка при сохранении одного из адресов выдачи',
    [RequestAddressesServiceTranslations.SetAddressError403]:
      'Подтвердить актуальный адрес выдачи может только специалист по сопровождению или другое уполномоченное лицо.',
    [RequestAddressesServiceTranslations.SetAddressError]:
      'Произошла ошибка при сохранении адреса выдачи',
    [RequestAddressesServiceTranslations.SetAddressErrorWarning]: 'Внимание!',
    [RequestAddressesServiceTranslations.SetAddressSuccess]:
      'Актуальный адрес выдачи сохранён! Выполняем повторную загрузку изменённых заявок',
    [RequestAddressesServiceTranslations.ResetAddressErrorPermission]:
      'У вас нет прав на сброс адреса выдачи',
    [RequestAddressesServiceTranslations.ResetAddressDialogLabel]:
      'Вы действительно хотите сбросить актуальный адрес выдачи?',
    [RequestAddressesServiceTranslations.ResetAddressDialogConfirm]:
      'Да, сбросить',
    [RequestAddressesServiceTranslations.ResetAddressDialogCancel]: 'Отмена',
    [RequestAddressesServiceTranslations.ResetAddressError]:
      'Произошла ошибка при сбросе адреса выдачи',
    [RequestAddressesServiceTranslations.ResetAddressErrorMultiAll]:
      'Произошла ошибка при сбросе всех адресов выдачи',
    [RequestAddressesServiceTranslations.ResetAddressErrorMultiSome]:
      'Произошла ошибка при сбросе одного из адресов выдачи',
    [RequestAddressesServiceTranslations.ResetAddressSuccess]:
      'Актуальный адрес выдачи сохранён! Выполняем повторную загрузку изменённых заявок',
  };

  constructor() {
    this.outkeepTranslateService
      .translate(Object.values(RequestAddressesServiceTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  setActualDeliveryAddress$(value: string, requests?: RequestModel[]) {
    let errorCount = 0;

    return this.dialogService$
      .open<boolean>(TUI_CONFIRM, {
        label:
          this.translates[
            RequestAddressesServiceTranslations.SetAddressDialogLabel
          ],
        size: 's',
        closable: false,
        data: {
          yes: this.translates[
            RequestAddressesServiceTranslations.SetAddressDialogConfirm
          ],
          no: this.translates[
            RequestAddressesServiceTranslations.SetAddressDialogCancel
          ],
        },
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          const requestsArr = requests ? requests : [this.$request()];

          return forkJoin(
            requestsArr.map(request =>
              this.requestApiService
                .setActualDeliveryAddress$(request.id, value)
                .pipe(
                  catchError((err: HttpErrorResponse) => {
                    if (err.status === 403) {
                      this.xAlertService.showMessage(
                        this.translates[
                          RequestAddressesServiceTranslations.SetAddressError403
                        ],
                        {
                          appearance: 'negative',
                          autoClose: 4000,
                        },
                      );
                    } else {
                      if (err.error) {
                        this.xAlertService.error(err.error);
                      }
                    }
                    errorCount++;

                    return of(null);
                  }),
                ),
            ),
          ).pipe(
            tap(() => {
              if (errorCount > 0) {
                let message;

                if (requests) {
                  if (errorCount === requests.length) {
                    message =
                      this.translates[
                        RequestAddressesServiceTranslations
                          .SetAddressErrorMultiAll
                      ];
                  } else {
                    message =
                      this.translates[
                        RequestAddressesServiceTranslations
                          .SetAddressErrorMultiSome
                      ];
                  }
                } else {
                  message =
                    this.translates[
                      RequestAddressesServiceTranslations.SetAddressError
                    ];
                }
                this.xAlertService.error(
                  this.translates[
                    RequestAddressesServiceTranslations.SetAddressErrorWarning
                  ],
                  message,
                  false,
                );
              } else {
                this.xAlertService.success(
                  this.translates[
                    RequestAddressesServiceTranslations.SetAddressSuccess
                  ],
                  '',
                  5000,
                );
              }
            }),
          );
        }),
      );
  }

  resetActualDeliveryAddress$(requests?: RequestModel[]) {
    let errorCount = 0;

    return this.permissionService.canLogistEditRequestsResetAddress$.pipe(
      switchMap((can: boolean) => {
        if (!can) {
          this.xAlertService.showMessage(
            this.translates[
              RequestAddressesServiceTranslations.ResetAddressErrorPermission
            ],
            {
              appearance: 'negative',
            },
          );

          return EMPTY;
        }

        return this.dialogService$
          .open<boolean>(TUI_CONFIRM, {
            label:
              this.translates[
                RequestAddressesServiceTranslations.ResetAddressDialogLabel
              ],
            size: 's',
            closable: false,
            data: {
              yes: this.translates[
                RequestAddressesServiceTranslations.ResetAddressDialogConfirm
              ],
              no: this.translates[
                RequestAddressesServiceTranslations.ResetAddressDialogCancel
              ],
            },
          })
          .pipe(
            filter(Boolean),
            switchMap(() => {
              const requestsArr = requests ? requests : [this.$request()];

              return forkJoin(
                requestsArr.map(request =>
                  this.requestApiService
                    .resetActualDeliveryAddress$(request.id)
                    .pipe(
                      catchError(() => {
                        errorCount++;

                        return of(null);
                      }),
                    ),
                ),
              ).pipe(
                tap(() => {
                  if (errorCount > 0) {
                    let message;

                    if (requests) {
                      if (errorCount === requests.length) {
                        message =
                          this.translates[
                            RequestAddressesServiceTranslations
                              .ResetAddressErrorMultiAll
                          ];
                      } else {
                        message =
                          this.translates[
                            RequestAddressesServiceTranslations
                              .ResetAddressErrorMultiSome
                          ];
                      }
                    } else {
                      message =
                        this.translates[
                          RequestAddressesServiceTranslations.ResetAddressError
                        ];
                    }
                    this.xAlertService.error(
                      this.translates[
                        RequestAddressesServiceTranslations
                          .SetAddressErrorWarning
                      ],
                      message,
                      false,
                    );
                  } else {
                    this.xAlertService.showMessage(
                      this.translates[
                        RequestAddressesServiceTranslations.ResetAddressSuccess
                      ],
                      {
                        appearance: 'positive',
                      },
                    );
                  }
                }),
              );
            }),
          );
      }),
    );
  }

  isDeliveryAddressDifferentFromActual(request?: RequestModel) {
    const req = request || this.$request();

    return (
      req.actualDeliveryAddress &&
      req.deliveryAddress &&
      req.actualDeliveryAddress !== req.deliveryAddress
    );
  }
}
